import {
  type ComponentType,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import type { RadioGroupProps } from "src/components/RadioGroup/RadioGroup.tsx";
import type { NumberInputProps } from "src/components/NumberInput/NumberInput.tsx";
import type { SelectProps } from "src/components/Select/Select.tsx";
import type { FieldPath, FieldValues } from "react-hook-form";
import type {
  GridFilterItem,
  GridFilterModel,
  GridRowParams,
} from "@mui/x-data-grid-premium";

import type { DisabledField, HiddenField } from "src/system/module.types.ts";
import type {
  Config,
  MediaAttributes,
} from "src/components/DropZoneInputV2/types";
import type { GridSortModel } from "@mui/x-data-grid/models";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import type { CodeEditorLanguage } from "src/components/CodeEditor/CodeEditor.types.ts";
import type { Interpolation } from "@emotion/styled";
import type { Theme } from "@emotion/react";
import type {
  PlaceholderPlacement,
  ResizeValues,
} from "src/components/TextArea/TextArea.types.ts";
import type { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts/PickersShortcuts";
import type { Dayjs } from "dayjs";
import type { SelectLabelProp, SelectValueProp } from "src/system/system.types";
import type { EditorConfig } from "ckeditor5";
import type { SwiperOptions } from "src/components/Swiper/Swiper.tsx";
import type { ChipV2Size } from "src/components/ChipV2/ChipV2.tsx";

type ValueSetter<
  FormValues extends FieldValues = FieldValues,
  V = any,
  R = V,
> = (value: V, values: FormValues) => R;

type CommonFormFieldProperties<
  FormValues extends FieldValues = FieldValues,
  Value = any,
  TransformedValue = Value,
> = {
  hidden?: HiddenField<FormValues>;
  relation?: string;
  disabled?: DisabledField<FormValues>;
  valueGetter?: (value: any, data: any) => Value;
  valueSetter?: ValueSetter<FormValues, Value, TransformedValue>;
  width?: string | number;
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: Value;
  dontSend?: boolean | { (value: Value): boolean };
};

export type TextField<FormValues extends FieldValues = FieldValues> = {
  type: "text";
  label?: string;
  translatable?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  placeholdersSize?: ChipV2Size;
  floatingError?: boolean;
  placeholders?:
    | TextTemplatePlaceholder[]
    | { (value: FormValues): TextTemplatePlaceholder[] };
  required?: boolean;
  additionalLabelElems?: ReactNode[];
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  readOnly?: boolean;
  labelWidth?: number;
} & CommonFormFieldProperties<FormValues, string>;

export type SwitchField<FormValues extends FieldValues = FieldValues> = {
  type: "switch";
  label: string;
  helperText?: ReactNode;
} & CommonFormFieldProperties<FormValues, boolean>;

export type NumberField<FormValues extends FieldValues = FieldValues> = {
  type: "number";
  label?: string;
  floatingError?: boolean;
  placeholder?: string;
  helperText?: ReactNode;
  precision?: number;
  endAdornment?: ReactNode;
  /**
   * Ширина концевой ячейки в пикселях; по умолчанию 35 — размер под символ
   * («%», «€»). Слову этого мало, и поле резервирует под ячейку ровно
   * столько же отступом, поэтому величина одна на обе стороны.
   */
  endAdornmentWidth?: number;
  controls?: boolean;
  required?: boolean;
  additionalLabelElems?: ReactNode[];
  emptyWhenZero?: boolean;
  min?: number;
  max?: number;
  /**
   * Eigene Darstellung des Werts. Ohne Angabe gruppiert das Zahlenfeld je drei Stellen,
   * was für Beträge richtig ist, für eine Jahreszahl aber „2 026“ ergibt. Felder, deren
   * Zahl keine Menge ist, geben hier eine eigene Darstellung an.
   */
  formatter?: NumberInputProps["formatter"];
} & CommonFormFieldProperties<FormValues, number | string | null>;

export type CustomField<FormValues extends FieldValues = FieldValues> = {
  type: "custom";
  Component?: ComponentType;
  element?: ReactElement;
} & CommonFormFieldProperties<FormValues, any>;

export type SingleSelectOptions =
  | SelectProps<any>["options"]
  | {
      resource: string;
      field: string;
      only?: string[];
    };

export type SingleSelectField<FormValues extends FieldValues = FieldValues> = {
  type: "singleSelect";
  displayAsText?: boolean;
  labelWidth?: number;
  allowClearValue?: boolean;
  label?: ReactNode;
  placeholder?: string;
  isButton?: boolean;
  helperText?: ReactNode;
  additionalLabelElems?: ReactNode[];
  floatingError?: boolean;
  serverOptions?: SingleSelectServerOptions;
  options?: SingleSelectOptions;
  onChange?: (value: any, form: any, resource: any) => void;
  /** Только отметка на подписи — правила валидации из неё не возникает. */
  required?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
} & CommonFormFieldProperties<FormValues, any>;

export type SelectField<FormValues extends FieldValues = FieldValues> = {
  type: "infiniteSelect";
  label?: string;
  allowClearValue?: boolean;
  resourceKey?: string;
  getResourcesForInitialOptions?: (mainResource: any) => any;

  getResourcesForInitialOptionsFromData?: (
    action: FormAction,
    data: any,
  ) => any;
  getInitialOptionsFromMainResource?: boolean;
  helperText?: ReactNode;
  isMultiple?: boolean;
  serverOptions?: SelectServerOptions;
  options?: SelectProps<any>["options"];
  floatingError?: boolean;
  onChange?: (
    value: string,
    resource: any,
    form: any,
  ) => boolean | void | undefined;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  showAdvancedSearch?: boolean;
  optionsMinWidth?: number;
  isButton?: boolean;
  placeholder?: string;
} & CommonFormFieldProperties<FormValues, any>;

export type MultiSelectField<FormValues extends FieldValues = FieldValues> = {
  type: "multiSelect";
  label?: string;
  showSearch?: boolean;
  helperText?: ReactNode;
  serverOptions?: SelectServerOptions;
  floatingError?: boolean;
  options?: SelectProps["options"];
} & CommonFormFieldProperties<FormValues, any[], any>;

export type CheckboxField<FormValues extends FieldValues = FieldValues> = {
  type: "checkbox";
  label?: string;
  onChange?: (value: boolean, form: any, name: string) => void;
  floatingError?: boolean;
  helperText?: ReactNode;
  additionalElements?: ReactNode[];
} & CommonFormFieldProperties<FormValues, boolean>;

export type ImageField<FormValues extends FieldValues = FieldValues> = {
  type: "image";
  label?: string;
  helperText?: ReactNode;
} & CommonFormFieldProperties<FormValues, any>;

export type ImageFieldV2<FormValues extends FieldValues = FieldValues> = {
  type: "imageV2";
  label?: string;
  disableDragging?: boolean;
  helperText?: ReactNode;
  disablePreviewImage?: boolean;
  validationRules?: Partial<Config["validationRules"]>;
  multiple?: boolean;
  maxItems?: number;
} & CommonFormFieldProperties<FormValues, { [uuid: string]: MediaAttributes }>;

export type RadioFieldOptions =
  | RadioGroupProps["options"]
  | {
      resource: string;
      field: string;
    };

export type RadioField<FormValues extends FieldValues = FieldValues> = {
  type: "radio";
  label?: string;
  orientation?: "vertical" | "horizontal";
  options: RadioFieldOptions;
} & CommonFormFieldProperties<FormValues, string | boolean | number, any>;

export type CkeditorField<FormValues extends FieldValues = FieldValues> = {
  type: "ckeditor";
  height?: CSSProperties["height"];
  translatable?: boolean;
  placeholders?:
    | TextTemplatePlaceholder[]
    | { (value: FormValues): TextTemplatePlaceholder[] };
  placeholdersSize?: ChipV2Size;
  label?: ReactNode;
  config?: EditorConfig;
} & CommonFormFieldProperties<FormValues, string>;

export type AutocompleteField<FormValues extends FieldValues = FieldValues> = {
  type: "autocomplete";
  label: string;
  searchFieldName: string;
  floatingError?: boolean;
  filterField: string;
  filterOperator?: string;
  placeholder?: string;
  valueProp?: SelectValueProp<any>;
  labelProp?: SelectLabelProp<any>;
  advancedSearch?: {
    title?: string;
    onRowClick?: (params: { form: any; params: GridRowParams<any> }) => void;
    labelProp?: string;
  };

  startSearchWith?: number;
  onValueChange?: (params: { value: string; item?: any; form: any }) => void;
  resource: string;
  include?: string[];
} & CommonFormFieldProperties<FormValues, string>;

/**
 * Поле для текста, в котором плейсхолдеры показываются чипами.
 *
 * Отличается от `textArea` не украшением: там токен — двадцать обычных
 * символов, и правится он посимвольно, здесь — один неделимый значок с
 * человеческим названием. Содержимое в обоих случаях обычный текст.
 */
export type CodeEditorField<FormValues extends FieldValues = FieldValues> = {
  type: "codeEditor";
  label?: string;
  translatable?: boolean;
  helperText?: ReactNode;
  /** Подсветка синтаксиса; `plain` — для текста без разметки, как SMS. */
  language?: CodeEditorLanguage;
  maxLength?: number;
  minHeight?: string;
  placeholder?: string;
  placeholdersPlacement?: PlaceholderPlacement;
  placeholders?:
    | TextTemplatePlaceholder[]
    | { (value: FormValues): TextTemplatePlaceholder[] };
  placeholdersSize?: ChipV2Size;
  width?: CSSProperties["width"];
  floatingError?: boolean;
} & CommonFormFieldProperties<FormValues, string>;

export type TextAreaField<FormValues extends FieldValues = FieldValues> = {
  type: "textArea";
  label?: string;
  translatable?: boolean;
  placeholdersPlacement?: PlaceholderPlacement;
  helperText?: ReactNode;
  resize?: ResizeValues;
  maxLength?: number;
  placeholders?:
    | TextTemplatePlaceholder[]
    | { (value: FormValues): TextTemplatePlaceholder[] };
  placeholdersSize?: ChipV2Size;
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  floatingError?: boolean;
  placeholder?: string;
} & CommonFormFieldProperties<FormValues, string>;

export type DateField<FormValues extends FieldValues = FieldValues> = {
  type: "date";
  mode?: "dateTime" | "date";
  label?: string;
  // `string`, not the `ReactNode` the other field types take: DatePicker has
  // always declared it as a string and hands it straight to InputLabel.
  helperText?: string;
  floatingError?: boolean;
} & CommonFormFieldProperties<FormValues, any>;

export type InputRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "inputRange";
  label: string;
  floatingError?: boolean;
  nameFrom: keyof FormValues & string;
  nameTo: keyof FormValues & string;
  additionalField?: keyof FormValues & string;
  shortcuts?: InputRangeShortcut[];
} & CommonFormFieldProperties<FormValues, any>;

export type GroupField<FormValues extends FieldValues = FieldValues> = {
  type: "group";
  floatingError?: boolean;
  title: string;
  fields_order: readonly (
    | (keyof FormValues & string)
    | readonly (keyof FormValues & string)[]
  )[];
} & CommonFormFieldProperties<FormValues, any>;

export type TimeField<FormValues extends FieldValues = FieldValues> = {
  type: "time";
  label: string;
  floatingError?: boolean;
} & CommonFormFieldProperties<FormValues, any>;

export type TimeRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "timeRange";
  label: string;
  floatingError?: boolean;
} & CommonFormFieldProperties<FormValues, any>;

export type InputRangeShortcut = {
  label?: ReactNode;
  getValue: () => [number | null, number | null];
  id: string;
};

export type DateRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "dateRange";
  label?: string;
  shortcuts?: PickersShortcutsItem<
    [string | null | Dayjs, string | null | Dayjs]
  >[];

  nameFrom: keyof FormValues & string;
  nameTo: keyof FormValues & string;

  floatingError?: boolean;
} & CommonFormFieldProperties<FormValues, [string | null, string | null]>;

export type SegmentedControlField<
  FormValues extends FieldValues = FieldValues,
> = {
  type: "segmentedControl";
  mirrorState?: keyof FormValues;
  options: SwiperOptions<string | boolean | number>;
  onChange?: (value: string | boolean | number, form: any) => void;
} & CommonFormFieldProperties<FormValues, string | boolean | number>;

export type ValidFormField<FormValues extends FieldValues = FieldValues> =
  | TextField<FormValues>
  | NumberField<FormValues>
  | SingleSelectField<FormValues>
  | CheckboxField<FormValues>
  | MultiSelectField<FormValues>
  | SelectField<FormValues>
  | CustomField<FormValues>
  | DateRangeField<FormValues>
  | RadioField<FormValues>
  | ImageField<FormValues>
  | ImageFieldV2<FormValues>
  | CkeditorField<FormValues>
  | AutocompleteField<FormValues>
  | SwitchField<FormValues>
  | TextAreaField<FormValues>
  | CodeEditorField<FormValues>
  | DateField<FormValues>
  | TimeField<FormValues>
  | TimeRangeField<FormValues>
  | InputRangeField<FormValues>
  | SegmentedControlField<FormValues>
  | GroupField<FormValues>;

export type SelectSearchField = string | string[] | GridFilterItem[];

export type SelectOption = {
  value: string;
  label: string;
};

export type SelectServerOptions = {
  resource: string;
  filter?:
    | GridFilterModel["items"]
    | { (values: any): GridFilterModel["items"] };
  filter2?:
    | GridFilterModel["items"]
    | { (values: any, modal: boolean): GridFilterModel["items"] };
  logicOperator?: GridFilterModel["logicOperator"];
  logicOperator2?: GridFilterModel["logicOperator"];
  sort?: GridSortModel;
  getCopyText?: (resource: any) => string;
  showButtons?: ("view" | "copy")[];
  labelProp?: SelectLabelProp<any>;
  withoutDescendants?: boolean;
  valueProp?: SelectValueProp<any>;
  processOptions?: (options: SelectOption[]) => SelectOption[];
  filterFn?: (
    option: SelectOption,
    index: number,
    options: SelectOption[],
  ) => boolean;
  include?: string[];
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  optionMetaField?: string;
};

export type SingleSelectServerOptions = {
  resource: string;
  filter?:
    | GridFilterModel["items"]
    | { (values: any): GridFilterModel["items"] };
  logicOperator?: GridFilterModel["logicOperator"];
  sort?: GridSortModel;
  labelProp?: SelectLabelProp<any>;
  valueProp?: SelectValueProp<any>;
  processOptions?: (
    options: {
      value: string;
      label: string;
    }[],
  ) => {
    value: string;
    label: string;
  }[];
  include?: string[];
  groupBy?: string;
  groups?: {
    label: string;
    value: string;
  }[];
};

export type ValidFormFields = Record<string, ValidFormField>;

export enum FormAction {
  Edit = "edit",
  Create = "create",
}

export type ValidArrayField<FormValues extends FieldValues = FieldValues> =
  | (keyof FormValues & string)
  | "";

export type FieldName<FormValues extends FieldValues = FieldValues> =
  | (keyof FormValues & string)
  | readonly ValidArrayField[];

export type FieldsOrder<FormValues extends FieldValues = FieldValues> =
  readonly FieldName<FormValues>[];

export type CustomFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "custom";
  Component: ComponentType<{
    formScrollContainerRef?: Ref<HTMLElement>;
  }>;
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type PriceFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "price";
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type DefaultFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "default";
  fields_order: FieldsOrder<FormValues>;
  icon?: ReactNode;
  title?: string;
  translatable?: boolean;
  gap?: number;
  helperText?: string;
  css?: Interpolation<Theme>;
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type CommentsFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "comments";

  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
  resource?: string;
};

export type HiddenPanelConfig<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> =
  | {
      watch_names?: readonly [...FieldNames];
      hide_condition: (values: FormValues) => boolean;
    }
  | { (values: FormValues): boolean }
  | boolean;

export type ContactsFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "contacts";

  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type TransferPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "transfer";
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
  resource: string;
};

export type ValidFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> =
  | CustomFormPanel<FormValues, FieldNames>
  | DefaultFormPanel<FormValues, FieldNames>
  | TransferPanel<FormValues, FieldNames>
  | CommentsFormPanel<FormValues, FieldNames>
  | ContactsFormPanel<FormValues, FieldNames>
  | PriceFormPanel<FormValues, FieldNames>;
